import { useState, useEffect } from "react";
import {
  useGetCartListQuery,
  useGetOvensQuery,
} from "../../processing/cooking/cookingApi";
import { useCreateGreenRoomOvenDataMutation, useUpdateGreenRoomOvenDataMutation } from "./greenRoomApi";
import { Col, Row } from "react-bootstrap";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import InputWithIcon from "../../../components/shared/fields/InputWithIcon";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import Button from "../../../components/shared/buttons/button";
import { RefreshSvg } from "../../../svgFiles/RefreshSvg";
import toast from "react-hot-toast";
import { isValidDate, newIncreasingDate } from "../../../components/shared/utils";
import useTranslator from "../../../hooks/useTranslator";
import { convertToUserTimezone, getCurrentDate, getCurrentDateTime, makePriorLotNumber } from "../../../helpers/Utils";

const GreenRoomOvenModal = ({ show, setShow, record, mode, type_name, date }) => {
  const { _t } = useTranslator();
  const [createData] = useCreateGreenRoomOvenDataMutation();
  const [updateData] = useUpdateGreenRoomOvenDataMutation();
  const [status] = useState("available");
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const lotNumber = makePriorLotNumber(currentDate);
  const shouldSelectType = mode === "add" && !type_name;
  const isViewOnly = mode === "view";

  const treyCountConfig = {
    "cheeks/strips": [
      { name: "cheeks_trey_count", label: _t("cheeks_trey_count", "Cheeks Trey Count") },
      { name: "strips_trey_count", label: _t("strips_trey_count", "Strips Trey Count") },
    ],
    "cheeks/tapita": [
      { name: "cheeks_trey_count", label: _t("cheeks_trey_count", "Cheeks Trey Count") },
      { name: "tapita_trey_count", label: _t("tapita_trey_count", "Tapita Trey Count") },
    ],
    "cheeks": [{ name: "cheeks_trey_count", label: _t("cheeks_trey_count", "Cheeks Trey Count") }],
    "strips": [{ name: "strips_trey_count", label: _t("strips_trey_count", "Strips Trey Count") }],
    "tapita": [{ name: "tapita_trey_count", label: _t("tapita_trey_count", "Tapita Trey Count") }],
  };

  const baggedFieldConfig = {
    "cheeks":        [{ name: "cheeks_kg_bagged",  label: _t("cheeks_kg_bagged",  "Cheeks KG Bagged") }],
    "strips":        [{ name: "strips_kg_bagged",  label: _t("strips_kg_bagged",  "Strips KG Bagged") }],
    "cheeks/strips": [
      { name: "cheeks_kg_bagged", label: _t("cheeks_kg_bagged", "Cheeks KG Bagged") },
      { name: "strips_kg_bagged", label: _t("strips_kg_bagged", "Strips KG Bagged") },
    ],
    "tapita":        [{ name: "tapita_kg_bagged",  label: _t("tapita_kg_bagged",  "Tapita KG Bagged") }],
    "cheeks/tapita": [
      { name: "cheeks_kg_bagged", label: _t("cheeks_kg_bagged", "Cheeks KG Bagged") },
      { name: "tapita_kg_bagged", label: _t("tapita_kg_bagged", "Tapita KG Bagged") },
    ],
  };

  const initialFormData = {
    date_time_entered: "",
    status: mode === "add" ? "cooking" : "",
    date: currentDate,
    treys_count: "",
    cheeks_trey_count: "",
    strips_trey_count: "",
    tapita_trey_count: "",
    time_end: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
    time_entered: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
    type: type_name || "",
    cart_number_id: "",
    oven_id: "",
    oven: {},
    cheeks_cart_weight: 0,
    strips_cart_weight: 0,
    bagged_fruit_weight: "",
    cheeks_kg_bagged: "",
    strips_kg_bagged: "",
    tapita_kg_bagged: "",
    cart_number: {},
    data_from: "rework",
    oven_batch_code: lotNumber,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [ovenId, setOvenId] = useState(null);

  useEffect(() => {
    if (mode === "edit" && record?.oven?.id) {
      setOvenId(record.oven.id);
    }
  }, [mode, record]);

  const { data: ovenList } = useGetOvensQuery({
    status,
    ...(ovenId ? { oven_id: ovenId } : {}),
  });
  const { data: cartsList, refetch: refetchCartList } = useGetCartListQuery({ status });

  useEffect(() => {
    if (mode === "edit" && record) {
      const updatedFormData = { ...initialFormData };

      for (const key in record) {
        if (key === "oven") updatedFormData["oven_id"] = record.oven.id;
        if (key === "type") updatedFormData["type_id"] = record.type.id;
        if (key === "cart_number")
          updatedFormData["cart_number_id"] = record.cart_number.id;
        if (key in initialFormData) updatedFormData[key] = record[key];
      }

      updatedFormData.time_entered = record.time_entered
        ? convertToUserTimezone(record.time_entered, "YYYY-MM-DDTHH:mm")
        : "";
      updatedFormData.time_end = record.time_end
        ? convertToUserTimezone(record.time_end, "YYYY-MM-DDTHH:mm")
        : "";
      setFormData(updatedFormData);
    } else {
      setFormData({
        ...initialFormData,
        time_entered: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
      });
    }
  }, [mode, record, show]);

  const isCartAvailable = (cart) =>
    cart.status === "available" || cart.id === record?.cart_number?.id;

  const handleClose = () => setShow(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.type) {
      toast.error(_t("select_type_error", "Please select type."));
      return;
    }

    const payload = {
      ...formData,
      cheeks_trey_count: parseInt(formData.cheeks_trey_count) || 0,
      strips_trey_count: parseInt(formData.strips_trey_count) || 0,
      tapita_trey_count: parseInt(formData.tapita_trey_count) || 0,
      treys_count: parseInt(formData.treys_count) || 0,
      cheeks_kg_bagged: parseFloat(formData.cheeks_kg_bagged) || 0,
      strips_kg_bagged: parseFloat(formData.strips_kg_bagged) || 0,
      tapita_kg_bagged: parseFloat(formData.tapita_kg_bagged) || 0,
      data_type : "green"
    };

    if (payload.oven_batch_code) {
      const value = payload.oven_batch_code;
      const dateRegex = /^(\d{2})(\d{2})(\d{2})$/;

      if (!dateRegex.test(value)) {
        toast.error(_t("invalid_batch_id_format", "Invalid format! Please enter Batch Id in DDMMYY format."));
        return;
      }
      const day = value.substring(0, 2);
      const month = value.substring(2, 4);
      const year = parseInt(value.substring(4, 6), 10) + 2000;

      if (!isValidDate(year, month, day)) {
        toast.error(_t("invalid_batch_id_date", "Invalid date! Please enter a correct Batch Id."));
        return;
      }
    }

    const mutation = mode === "add" ? createData : updateData;

    try {
      await mutation(
        mode === "add" ? payload : { id: record.id, requestBody: payload }
      ).unwrap();
      refetchCartList();
      handleClose();
    } catch (error) {
      console.error("Error creating/updating green room oven data:", error);
    }
  };

  const TREY_FIELDS = ["cheeks_trey_count", "strips_trey_count", "tapita_trey_count"];

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "type") {
      setFormData((prev) => ({
        ...prev,
        cheeks_trey_count: "",
        strips_trey_count: "",
        tapita_trey_count: "",
        treys_count: 0,
      }));
    }

    if ((name === "time_entered" && formData.type !== "") || name === "type") {
      const selectedType = name === "type" ? value : formData.type;
      const defaultTime = name === "time_entered" ? value : formData.time_entered;

      const minutesMap = {
        "cheeks": 690,
        "strips": 570,
        "cheeks/strips": 570,
        "tapita": 690,
        "cheeks/tapita": 690,
      };

      const minutes = minutesMap[selectedType];
      if (minutes) {
        const expEndTime = newIncreasingDate(minutes, defaultTime);
        setFormData((prev) => ({ ...prev, time_end: expEndTime }));
      }
    }

    if (TREY_FIELDS.includes(name)) {
      const updated = { ...formData, [name]: value };
      const total = TREY_FIELDS.reduce((sum, key) => sum + (parseInt(updated[key]) || 0), 0);
      setFormData((prev) => ({ ...prev, [name]: value, treys_count: total }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <SelectBox
          label={_t("select_oven", "Select Oven")}
          name="oven_id"
          value={formData.oven_id}
          options={(ovenList?.data || []).map((list) => ({ value: list.id, label: _t(list.name, list.name) }))}
          onChange={handleChange}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel label={_t("oven_batch_code", "Oven Batch Code")} type="text" name="oven_batch_code" value={formData.oven_batch_code} onChange={handleChange} disabled={true} />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("carts_number", "Carts Number")}
          name="cart_number_id"
          value={formData.cart_number_id}
          options={(cartsList?.data || []).map((list) => ({
            value: list.id,
            label: list.name,
            disabled: !isCartAvailable(list),
          }))}
          onChange={handleChange}
          disabled={mode === "edit" || isViewOnly}
        />
      </Col>
      <Col lg={6}>
        {shouldSelectType ? (
          <SelectBox
            label={_t("type", "Type")}
            name="type"
            value={formData.type}
            options={[
              { value: "cheeks", label: _t("type_cheeks", "Cheeks") },
              { value: "strips", label: _t("type_strips", "Strips") },
              { value: "cheeks/strips", label: _t("type_cheeks_strips", "Cheeks/Strips") },
              { value: "tapita", label: _t("type_tapita", "Tapita") },
              { value: "cheeks/tapita", label: _t("type_cheeks_tapita", "Cheeks/Tapita") },
            ]}
            onChange={handleChange}
          />
        ) : (
          <InputWithLabel label={_t("type", "Type")} type="text" name="type" value={formData.type} onChange={handleChange} disabled={true} />
        )}
      </Col>
      <Col lg={6}>
        <InputWithLabel label={_t("time_entered", "Time Entered")} type="datetime-local" name="time_entered" value={formData.time_entered} onChange={handleChange} disabled={true} />
      </Col>
      <Col lg={6}>
        <InputWithLabel label={_t("time_end", "Time End")} type="datetime-local" name="time_end" value={formData.time_end} onChange={handleChange} disabled={isViewOnly} />
      </Col>
      {treyCountConfig[formData.type] ? (
        treyCountConfig[formData.type].map((field) => (
          <Col lg={6} key={field.name}>
            <InputWithLabel label={field.label} type="number" name={field.name} value={formData[field.name]} onChange={handleChange} placeholder={"0"} min={0} disabled={mode === "edit" || isViewOnly} />
          </Col>
        ))
      ) : (
        <Col lg={6}>
          <InputWithLabel label={_t("treys_count", "Treys Count")} type="number" name="treys_count" value={formData.treys_count} onChange={handleChange} disabled={mode === "edit" || isViewOnly} placeholder={"0"} />
        </Col>
      )}
      <Col lg={6}>
        <SelectBox
          label={_t("status", "Status")}
          name="status"
          value={formData?.status}
          disabled={isViewOnly}
          options={
            mode === "add"
              ? [
                  { value: "cooking", label: _t("cooking", "Cooking") },
                  { value: "cooling_room", label: _t("cooling_room", "Cooling Room") },
                ]
              : [
                  { value: "cooking", label: _t("cooking", "Cooking"), disabled: true },
                  { value: "cooling_room", label: _t("cooling_room", "Cooling Room") },
                  { value: "cart_unloaded", label: _t("cart_unloaded", "Cart Unloaded") },
                ]
          }
          onChange={handleChange}
        />
      </Col>
      {formData.status === "cart_unloaded" &&
        (baggedFieldConfig[formData.type] || []).map((field) => (
          <Col lg={6} key={field.name}>
            <InputWithIcon
              icon={RefreshSvg}
              label={field.label}
              type="number"
              name={field.name}
              value={formData[field.name] || ""}
              onChange={handleChange}
              placeholder={"0"}
            />
          </Col>
        ))
      }
    </>
  );

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        mode === "add"
          ? _t("add_green_room_title", "Adding Green Room Data")
          : _t("edit_green_room_title", "Edit Green Room Data")
      }
      children={formData}
    >
      <Row>{renderFormFields()}</Row>
      <Row>
        <Col className="text-end">
          {!isViewOnly && (
            <Button
              label={_t("save_changes", "Save Changes")}
              size="small"
              onClick={handleSubmit}
            />
          )}
        </Col>
      </Row>
    </CommonModal>
  );
};

export default GreenRoomOvenModal;
